import React, { useState, useEffect, useRef } from "react";
import { RefreshCw, Check, ChevronDown, ChevronUp, X, Calendar, Filter, ChevronLeft, ChevronRight } from "lucide-react";
import axios from "axios";
import { useSelector } from "react-redux";
import { format, addMonths, startOfMonth, endOfMonth, parse, isAfter, isBefore, isSameDay } from "date-fns";
import { jwtDecode } from "jwt-decode";

const CustomDateRangePicker = ({
  startDate,
  endDate,
  onChange,
  placeholder = "Select date range"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [tempStartDate, setTempStartDate] = useState(null);
  const [tempEndDate, setTempEndDate] = useState(null);
  const [isSelectingStart, setIsSelectingStart] = useState(true);
  const datePickerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
        // Reset temp dates when closing without applying
        setTempStartDate(null);
        setTempEndDate(null);
        setIsSelectingStart(true);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return format(date, 'yyyy-MM-dd');
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'MMM dd, yyyy');
  };

  const handleDateSelect = (date) => {
    if (isSelectingStart || !tempStartDate) {
      setTempStartDate(date);
      setTempEndDate(null);
      setIsSelectingStart(false);
    } else {
      if (isBefore(date, tempStartDate)) {
        // If selected date is before start date, make it the new start date
        setTempStartDate(date);
        setTempEndDate(null);
      } else {
        setTempEndDate(date);
      }
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prevMonth => {
      const newMonth = new Date(prevMonth);
      newMonth.setMonth(newMonth.getMonth() + direction);
      return newMonth;
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date && isSameDay(date, today);
  };

  const isInRange = (date) => {
    if (!tempStartDate || !tempEndDate || !date) return false;
    return (isAfter(date, tempStartDate) || isSameDay(date, tempStartDate)) &&
      (isBefore(date, tempEndDate) || isSameDay(date, tempEndDate));
  };

  const isStartDate = (date) => {
    return tempStartDate && date && isSameDay(date, tempStartDate);
  };

  const isEndDate = (date) => {
    return tempEndDate && date && isSameDay(date, tempEndDate);
  };

  const applyDateRange = () => {
    if (tempStartDate && tempEndDate) {
      onChange({
        startDate: formatDate(tempStartDate),
        endDate: formatDate(tempEndDate)
      });
      setIsOpen(false);
      setTempStartDate(null);
      setTempEndDate(null);
      setIsSelectingStart(true);
    }
  };

  const clearSelection = () => {
    setTempStartDate(null);
    setTempEndDate(null);
    setIsSelectingStart(true);
  };

  const setPresetRange = (preset) => {
    const now = new Date();
    let fromDate, toDate;

    switch (preset) {
      case "currentMonth":
        fromDate = startOfMonth(now);
        toDate = now;
        break;
      case "previousMonth":
        fromDate = startOfMonth(addMonths(now, -1));
        toDate = endOfMonth(addMonths(now, -1));
        break;
      case "previous3Months":
        fromDate = startOfMonth(addMonths(now, -3));
        toDate = endOfMonth(addMonths(now, -1));
        break;
      case "previous6Months":
        fromDate = startOfMonth(addMonths(now, -6));
        toDate = endOfMonth(addMonths(now, -1));
        break;
      case "previous12Months":
        fromDate = startOfMonth(addMonths(now, -12));
        toDate = endOfMonth(addMonths(now, -1));
        break;
      default:
        return;
    }

    setTempStartDate(fromDate);
    setTempEndDate(toDate);
  };

  const getDisplayText = () => {
    if (startDate && endDate) {
      return `${formatDisplayDate(startDate)} — ${formatDisplayDate(endDate)}`;
    }
    return placeholder;
  };

  const days = getDaysInMonth(currentMonth);

  return (
    <div className="relative" ref={datePickerRef}>
      <div
        className="w-full p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-opacity-20 transition-colors bg-white cursor-pointer flex items-center justify-between"
        style={{
          '--tw-ring-color': '#003366',
          borderColor: isOpen ? '#003366' : undefined
        }}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={startDate && endDate ? "text-gray-900 text-sm" : "text-gray-400 text-sm"}>
          {getDisplayText()}
        </span>
        <div className="flex items-center space-x-1">
          <Calendar className="w-4 h-4 text-gray-600" />
          {isOpen ?
            <ChevronUp className="w-4 h-4 text-gray-600" /> :
            <ChevronDown className="w-4 h-4 text-gray-600" />
          }
        </div>
      </div>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-md z-50 w-72">
          {/* Preset buttons */}
          <div className="p-2 border-b border-gray-100">
            <div className="grid grid-cols-3 gap-1 text-xs">
              <button
                type="button"
                onClick={() => setPresetRange("currentMonth")}
                className="px-2 py-1 text-gray-700 hover:bg-gray-50 rounded transition-colors text-left"
              >
                Current
              </button>
              <button
                type="button"
                onClick={() => setPresetRange("previousMonth")}
                className="px-2 py-1 text-gray-700 hover:bg-gray-50 rounded transition-colors text-left"
              >
                Last Month
              </button>
              <button
                type="button"
                onClick={() => setPresetRange("previous3Months")}
                className="px-2 py-1 text-gray-700 hover:bg-gray-50 rounded transition-colors text-left"
              >
                3 Months
              </button>
              <button
                type="button"
                onClick={() => setPresetRange("previous6Months")}
                className="px-2 py-1 text-gray-700 hover:bg-gray-50 rounded transition-colors text-left"
              >
                6 Months
              </button>
              <button
                type="button"
                onClick={() => setPresetRange("previous12Months")}
                className="px-2 py-1 text-gray-700 hover:bg-gray-50 rounded transition-colors text-left"
              >
                12 Months
              </button>
            </div>
          </div>

          {/* Calendar Header */}
          <div className="flex items-center justify-between p-2 border-b border-gray-100">
            <button
              type="button"
              onClick={() => navigateMonth(-1)}
              className="p-1 hover:bg-gray-50 rounded-full transition-colors"
            >
              <ChevronLeft className="w-3 h-3 text-gray-600" />
            </button>

            <span className="font-medium text-gray-900 text-sm">
              {months[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </span>

            <button
              type="button"
              onClick={() => navigateMonth(1)}
              className="p-1 hover:bg-gray-50 rounded-full transition-colors"
            >
              <ChevronRight className="w-3 h-3 text-gray-600" />
            </button>
          </div>

          {/* Selection instruction */}
          <div className="px-2 py-1 bg-gray-50 text-xs text-gray-700">
            {isSelectingStart || !tempStartDate ?
              'Select start date' :
              (tempEndDate ?
                `${formatDisplayDate(formatDate(tempStartDate))} — ${formatDisplayDate(formatDate(tempEndDate))}` :
                `Start: ${formatDisplayDate(formatDate(tempStartDate))} • Select end date`
              )
            }
          </div>

          {/* Days of week */}
          <div className="grid grid-cols-7 gap-1 p-1 border-b border-gray-100">
            {daysOfWeek.map(day => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-1">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar days */}
          <div className="grid grid-cols-7 gap-1 p-1">
            {days.map((date, index) => {
              const isStart = isStartDate(date);
              const isEnd = isEndDate(date);
              const inRange = isInRange(date);
              const today = isToday(date);

              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => date && handleDateSelect(date)}
                  disabled={!date}
                  className={`
                    h-7 w-7 text-xs rounded-md transition-colors flex items-center justify-center
                    ${!date ? 'invisible' : ''}
                    ${isStart || isEnd
                      ? 'text-white font-medium'
                      : inRange
                        ? 'text-gray-900'
                        : today
                          ? 'bg-gray-100 text-gray-900 font-medium border border-gray-300'
                          : 'text-gray-700 hover:bg-gray-50'
                    }
                  `}
                  style={{
                    backgroundColor: isStart || isEnd ? '#003366' : inRange ? '#e6f0ff' : undefined
                  }}
                >
                  {date?.getDate()}
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center p-2 border-t border-gray-100">
            <button
              type="button"
              onClick={clearSelection}
              className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
            >
              Clear
            </button>
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={applyDateRange}
                disabled={!tempStartDate || !tempEndDate}
                className="text-xs text-white px-2 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: !tempStartDate || !tempEndDate ? '#9ca3af' : '#003366',
                  borderColor: '#003366'
                }}
                onMouseEnter={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = '#004080';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!e.target.disabled) {
                    e.target.style.backgroundColor = '#003366';
                  }
                }}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CostFilters = ({
  filters,
  handleFilterChange,
  setAccountDetails,
  setError,
  accountDetails,
  availableFilters
}) => {
  const [tempSelections, setTempSelections] = useState({});
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const accessToken = useSelector((state) => state.accessToken.value);
  const idToken = useSelector((state) => state.idToken.value);
  const apiUrl = process.env.REACT_APP_API_URL;

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch account data
  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        let userEmail = "";
        try {
          const decoded = jwtDecode(idToken);
          userEmail = decoded.email || "";
        } catch (err) {
          console.error("Error decoding token:", err);
        }

        const epQuery = `
          query GetUserEps($email: String!) {
            getUserAssociatedEps(email: $email) {
              EpNo
              Oem
            }
          }
        `;
        const epResponse = await axios.post(
          apiUrl,
          {
            query: epQuery,
            variables: { email: userEmail }
          },
          { headers: { Authorization: accessToken } }
        );

        const userAssociatedEps = epResponse.data.data.getUserAssociatedEps || [];
        const userEpNos = userAssociatedEps.map(uep => uep.EpNo);

        const accountQuery = `
          query MyQuery {
            getAWSAccounts {
              AccountName
              AccountNo
              id
              EpNo
            }
          }
        `;
        const accountResponse = await axios.post(
          apiUrl,
          { query: accountQuery },
          {
            headers: { Authorization: accessToken }
          }
        );
        const allAccounts = accountResponse.data.data.getAWSAccounts || [];

        const accounts = allAccounts.filter(account => userEpNos.includes(account.EpNo));

        setAccountDetails(accounts);
        const filteredAccountNames = accounts.map(account => account.AccountName);
        setTempSelections(prevSelections => ({
          ...prevSelections,
          accountName: filteredAccountNames,
        }));
        handleFilterChange({
          target: {
            name: "accountName",
            value: filteredAccountNames,
          },
        });
      } catch (error) {
        console.error("Error fetching account data:", error);
        setError("Failed to fetch account data");
      }
    };

    if (accessToken) {
      fetchAccountData();
    }
  }, [accessToken, idToken, apiUrl]);

  // Handle date range changes
  const handleDateRangeChange = (dateRange) => {
    handleFilterChange({
      target: {
        name: "dateRange",
        value: dateRange
      }
    });
  };

  // Get unique values for filter dropdowns
  const getUniqueValues = (field) => {
    switch (field) {
      case "account_id":
      case "account_name":
        return accountDetails?.map(account => account.AccountName) || ["all"];

      case "region":
        return availableFilters.regions;

      case "service":
        return availableFilters.services;

      case "dimension":
        return ["account", "service", "region"];

      case "granularity":
        return ["daily", "month"];

      case "apiOperations":
        return availableFilters.apiOperations;

      default:
        return [];
    }
  };

  // Toggle dropdown open/closed state
  const toggleDropdown = (name) => {
    if (openDropdown !== name) {
      setTempSelections({
        ...tempSelections,
        [name]: Array.isArray(filters[name]) ?
          [...filters[name]] :
          (filters[name] ? [filters[name]] : [])
      });
    }
    setOpenDropdown(openDropdown === name ? null : name);
  };

  // Update temporary selections for multi-select filters
  const handleMultiSelectTemp = (name, value) => {
    let currentValues = tempSelections[name] || [];

    if (currentValues.includes(value)) {
      currentValues = currentValues.filter(v => v !== value);
    } else {
      currentValues.push(value);
    }

    setTempSelections({
      ...tempSelections,
      [name]: currentValues
    });
  };

  // Toggle select all functionality
  const toggleSelectAll = (name, options) => {
    const allSelected = options.every(option =>
      tempSelections[name]?.includes(option)
    );

    if (allSelected) {
      setTempSelections({
        ...tempSelections,
        [name]: []
      });
    } else {
      setTempSelections({
        ...tempSelections,
        [name]: [...options]
      });
    }
  };

  // Apply the temporary selections to the actual filters
  const applySelections = (name) => {
    handleFilterChange({
      target: {
        name: name,
        value: tempSelections[name]?.length > 0 ? tempSelections[name] : []
      }
    });
    setOpenDropdown(null);
  };

  // Remove a single selected item
  const removeSelectedItem = (name, value) => {
    const currentValues = Array.isArray(filters[name]) ?
      [...filters[name]] :
      (filters[name] ? [filters[name]] : []);

    const updatedValues = currentValues.filter(v => v !== value);

    handleFilterChange({
      target: {
        name: name,
        value: updatedValues.length > 0 ? updatedValues : []
      }
    });
  };

  // Render multi-select dropdown
  const renderDropdown = (name, label, options) => {
    const selectedValues = Array.isArray(filters[name]) ?
      filters[name] :
      (filters[name] ? [filters[name]] : []);

    const tempValues = openDropdown === name ?
      (tempSelections[name] || []) :
      selectedValues;

    const isOpen = openDropdown === name;

    return (
      <div className="relative mb-2">
        <label className="block text-sm font-medium text-gray-900 mb-1">
          {label}
        </label>
        <button
          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg flex justify-between items-center hover:border-gray-400 focus:outline-none transition-colors"
          style={{
            borderColor: isOpen ? '#003366' : undefined,
            '--tw-ring-color': '#003366'
          }}
          onClick={() => toggleDropdown(name)}
        >
          <span className="text-sm text-gray-700 truncate">
            {selectedValues.length > 0 ?
              `${selectedValues.length} selected` :
              `Select ${label}${label.endsWith('s') ? '' : 's'}`
            }
          </span>
          {isOpen ?
            <ChevronUp className="h-4 w-4 text-gray-600" /> :
            <ChevronDown className="h-4 w-4 text-gray-600" />
          }
        </button>

        {/* Selected tags display */}
        {selectedValues.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {selectedValues.length <= 2 ? (
              selectedValues.map(value => (
                <div key={value} className="bg-gray-100 border border-gray-200 text-gray-800 text-xs px-2 py-0.5 rounded-full flex items-center">
                  <span className="truncate max-w-24">{value}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelectedItem(name, value);
                    }}
                    className="ml-1 text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))
            ) : (
              <>
                <div className="bg-gray-100 border border-gray-200 text-gray-800 text-xs px-2 py-0.5 rounded-full flex items-center">
                  <span className="truncate max-w-24">{selectedValues[0]}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      removeSelectedItem(name, selectedValues[0]);
                    }}
                    className="ml-1 text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
                <div className="bg-gray-100 border border-gray-200 text-gray-800 text-xs px-2 py-0.5 rounded-full">
                  +{selectedValues.length - 1} more
                </div>
                <button
                  onClick={() => handleFilterChange({
                    target: { name, value: [] }
                  })}
                  className="text-xs text-gray-600 hover:underline ml-1"
                >
                  Clear all
                </button>
              </>
            )}
          </div>
        )}

        {isOpen && (
          <div className="absolute z-30 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-md overflow-hidden" ref={dropdownRef}>
            {/* Select All option */}
            {(name === "services" || name === "regions" || name === "apiOperations") ? (
              <div className="px-3 py-2 bg-blue-50 border-b border-gray-100">
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: '#003366' }}
                  ></div>
                  <span className="font-medium text-xs text-gray-700 uppercase tracking-wide">Include Only</span>
                </div>
              </div>
            ) : (
              // Keep the existing Select All for accounts
              <div
                className="px-3 py-2 hover:bg-gray-50 flex items-center cursor-pointer border-b border-gray-100"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleSelectAll(name, options);
                }}
              >
                <div
                  className={`w-4 h-4 border rounded mr-2 flex items-center justify-center ${options.length > 0 && tempValues.length === options.length ? 'border-transparent' :
                    (tempValues.length > 0 && tempValues.length < options.length ? 'border-transparent' : 'border-gray-300')
                    }`}
                  style={{
                    backgroundColor: options.length > 0 && tempValues.length === options.length ? '#003366' :
                      (tempValues.length > 0 && tempValues.length < options.length ? '#003366' : 'transparent')
                  }}
                >
                  {options.length > 0 && tempValues.length === options.length && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                  {tempValues.length > 0 && tempValues.length < options.length && (
                    <div className="h-1.5 w-1.5 bg-white"></div>
                  )}
                </div>
                <span className="font-medium text-sm text-gray-900">Select All</span>
              </div>
            )}

            {/* Filter options - Fixed scrolling issue */}
            <div className="max-h-32 overflow-y-auto">
              {options.map((option) => (
                <div
                  key={option}
                  className="px-3 py-1 hover:bg-gray-50 flex items-center cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleMultiSelectTemp(name, option);
                  }}
                >
                  <div
                    className={`w-4 h-4 border rounded mr-2 flex items-center justify-center ${tempValues.includes(option) ? 'border-transparent' : 'border-gray-300'}`}
                    style={{
                      backgroundColor: tempValues.includes(option) ? '#003366' : 'transparent'
                    }}
                  >
                    {tempValues.includes(option) && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <span className="truncate text-sm text-gray-700">{option}</span>
                </div>
              ))}
            </div>

            {/* Apply button */}
            <div className="border-t border-gray-100 p-2 flex justify-end bg-white">
              <button
                className="text-white py-1 px-3 text-sm rounded-lg transition-colors"
                style={{ backgroundColor: '#003366' }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#004080';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#003366';
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  applySelections(name);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm flex flex-col border border-gray-100" style={{ height: 'fit-content', maxHeight: '70vh' }}>
      {/* Filter header */}
      <div className="px-3 py-2 flex items-center border-b border-gray-100">
        <div className="flex items-center">
          <div
            className="w-1 h-4 rounded-full mr-2"
            style={{ backgroundColor: '#003366' }}
          ></div>
          <Filter className="h-4 w-4 text-gray-600 mr-2" />
          <h2 className="text-base font-semibold text-gray-900">Filters</h2>
        </div>
      </div>

      <div className="px-3 py-2 overflow-visible flex-grow space-y-3">
        {/* Date Range */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            Date Range
          </label>
          <CustomDateRangePicker
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={handleDateRangeChange}
            placeholder="Select date range"
          />
        </div>

        <div className="border-t border-gray-100 pt-3">
          <h3 className="text-sm font-semibold mb-2 text-gray-900">Resource Filters</h3>

          {/* Account filter */}
          {renderDropdown("accountName", "Account", getUniqueValues("account_id"))}

          {/* Service filter */}
          {renderDropdown("services", "Service", getUniqueValues("service"))}

          {/* Region filter */}
          {renderDropdown("regions", "Region", getUniqueValues("region"))}

          {/* API Operations filter */}
          {renderDropdown("apiOperations", "API Operation", getUniqueValues("apiOperations"))}
        </div>
      </div>
    </div>
  );
};

export default CostFilters;